import { translateBackend } from '@assets/i18n/config';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { kebabCase } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreateModelPortfolioOptions,
  ModelPortfolioData,
} from '../createModelPortfolio.types';

type ChoosePortfolioTypeProps = {
  options: CreateModelPortfolioOptions['choosePortfolioType'];
  modelPortfolioData: ModelPortfolioData;
  loading: boolean;
  updateModelPortfolio: (data: Partial<ModelPortfolioData>) => void;
  onContinue: () => void | Promise<void>;
  workflowCompletion?: any;
};

export const ChoosePortfolioType = ({
  options,
  modelPortfolioData,
  loading,
  updateModelPortfolio,
  onContinue,
  workflowCompletion,
}: ChoosePortfolioTypeProps) => {
  const { t } = useTranslation('shared');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [focused, setFocused] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);

  const fieldOptions: FieldOptions = useMemo(
    () => ({
      creationType: { required: true },
    }),
    [],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        creationType: modelPortfolioData.creationType,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, modelPortfolioData],
  );

  const submit = async () => {
    const formErrors = validate();

    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }

    if (!formErrors) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsCreateModelPortfolioContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );

      // Track step 1 next analytics
      sendAnalytic(ovAnalyticsEvents.modelPageCreateStep1Next, {
        creationType: modelPortfolioData.creationType,
        timestamp: new Date().toISOString(),
      });

      await Promise.resolve(onContinue());
    }
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='choose-portfolio-type-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || !modelPortfolioData.creationType,
        dataTestId: `create-model-portfolio-${kebabCase(translateBackend(options.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography
          variant='displayLarge'
          sx={{
            mt: 1,
            mb: 2,
            fontWeight: 400,
            fontSize: '40px',
            fontFamily: 'Tiempos Text',
          }}
          data-testid='choose-portfolio-type-title'
        >
          {translateBackend(options.title)}
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          sx={{ mb: 2 }}
          data-testid='choose-portfolio-type-subtitle'
        >
          {translateBackend(options.subtitle)}
        </Typography>
      )}

      <SelectionTile
        testId='create-model-portfolio'
        onChange={(e: any) => {
          updateModelPortfolio({
            ...modelPortfolioData,
            creationType: e.target.value,
          });

          // Track creation type selection analytics
          if (e.target.value === 'FROM_SCRATCH') {
            sendAnalytic(ovAnalyticsEvents.modelPageCreateStep1BuildScratch, {
              timestamp: new Date().toISOString(),
            });
          } else if (e.target.value === 'FROM_TEMPLATE') {
            sendAnalytic(
              ovAnalyticsEvents.modelPageCreateStep1BuildAssetTemplate,
              {
                timestamp: new Date().toISOString(),
              },
            );
          }
        }}
        value={modelPortfolioData.creationType ?? ''}
        options={options?.options?.map((option: any) => ({
          value: option.value,
          label: translateBackend(option.label),
          subtitle: translateBackend(option.description),
        }))}
        error={errors?.creationType?.active}
      />
    </WorkflowCompletionForm>
  );
};
